<ng-container *ngIf="state$ | async as state">
    <div mat-dialog-title class="mat-dialog-header">
        <h2 class="dialog-title">Schedule Evaluations</h2>
        <h3 *ngIf="!_data.defaults?.massAction; else bulkSampleCodes" class="dialog-subtitle">Sample: {{ state.sample?.code }}</h3>
        <ng-template #bulkSampleCodes>
            <h3 class="dialog-subtitle">Scheduling Evaluations for {{ state.bulkKeys ? state.bulkKeys.length : '' }} samples</h3>
        </ng-template>
    </div>

    <div mat-dialog-content>

        <form class="pv-form"
              [formGroup]="formGroup"
              #form="ngForm"
              novalidate
              autocomplete="false"
              (ngSubmit)="attempt()">

            <div class="form-column">

                <h3 class="form-subheader">Schedule</h3>

                <mat-form-field appearance="standard" *ngIf="!isMultiCrop">
                    <mat-label>Storage Regime **</mat-label>
                    <mat-select formControlName="scheduleId">
                        <mat-optgroup label="Library schedules" *ngIf="scheduleOptions.length">
                            <mat-option *ngFor="let schedule of scheduleOptions" [value]="schedule.id">{{ schedule.label }}</mat-option>
                        </mat-optgroup>
                        <mat-optgroup label="Custom regimes" *ngIf="customRegimeOptions.length">
                            <mat-option *ngFor="let regime of customRegimeOptions" [value]="customRegimePrefix + regime.key">{{ regime.config.label }}</mat-option>
                        </mat-optgroup>
                    </mat-select>
                    <mat-hint>Apply storage schedule according to regime</mat-hint>
                    <mat-error>Schedule required</mat-error>
                </mat-form-field>

                <p class="form-caption" *ngIf="!isMultiCrop && !scheduleOptions.length && !customRegimeOptions.length">
                    No schedules are available for this crop.
                </p>

                <ng-container *ngIf="isMultiCrop">

                    <p class="form-caption" *ngIf="allCropsHaveNoSchedules">
                        {{ allCropsHaveNoSchedulesMessage }}
                    </p>

                    <ng-container formArrayName="cropSchedules">
                        <ng-container *ngFor="let cropEntry of scheduleOptionsByCrop; let i = index" [formGroupName]="i">
                            <mat-form-field appearance="standard" *ngIf="cropEntry.schedules.length; else noSchedule">
                                <mat-label>{{ cropEntry.cropLabel }} Storage Regime **</mat-label>
                                <mat-select formControlName="scheduleId">
                                    <mat-option *ngFor="let schedule of cropEntry.schedules" [value]="schedule.id">{{ schedule.label }}</mat-option>
                                </mat-select>
                                <mat-hint>Apply storage schedule according to regime for {{ cropEntry.cropLabel }}</mat-hint>
                                <mat-error>Schedule required</mat-error>
                            </mat-form-field>
                            <ng-template #noSchedule>
                                <p class="form-caption">
                                    No storage schedule is defined for {{ cropEntry.cropLabel }} — its samples will be excluded from this submission.
                                </p>
                            </ng-template>
                        </ng-container>
                    </ng-container>

                </ng-container>

                <mat-form-field appearance="standard">
                    <mat-label>Schedule Start Date</mat-label>
                    <input matInput formControlName="startDate"
                           [matDatepicker]="startDatePicker" placeholder="YYYY-MM-DD" required>
                    <mat-datepicker-toggle matSuffix [for]="startDatePicker">
                    </mat-datepicker-toggle>
                    <mat-datepicker #startDatePicker></mat-datepicker>
                    <mat-hint>Storage regime start</mat-hint>
                    <mat-error>Start date required, formatted <em>YYYY-MM-DD</em></mat-error>
                </mat-form-field>

                <mat-form-field appearance="standard">
                    <mat-label>Duration</mat-label>
                    <input matInput type="number" formControlName="duration" required>
                    <span matSuffix>days</span>
                    <mat-hint>Duration [days] of each evaluation</mat-hint>
                    <mat-error>Enter a duration</mat-error>
                </mat-form-field>

                <mat-form-field appearance="standard">
                    <mat-label>Evaluation Protocol ***</mat-label>
                    <mat-select formControlName="protocolId">
                        <mat-select-trigger>
                            <ng-container *ngIf="protocolControl.value">
                                <span>{{ protocolControl.value | labelProtocol }}</span>
                            </ng-container>
                        </mat-select-trigger>

                        <mat-option *ngFor="let proto of state.protocolOptions" [value]="proto.id">
                            <mat-icon *ngIf="proto.favourite">star</mat-icon>
                            <span>{{ proto.title }}</span>
                        </mat-option>
                    </mat-select>
                    <mat-hint>Protocol applied to scheduled evaluations</mat-hint>
                    <mat-error>Protocol required</mat-error>
                </mat-form-field>

                <mat-form-field appearance="standard">
                    <mat-label>Sample Size</mat-label>
                    <input matInput type="number" formControlName="size" required>
                    <span matSuffix>#</span>
                    <mat-hint>Number of trees/fruit/vines in the sample</mat-hint>
                    <mat-error>Enter a sample size [min 1]</mat-error>
                </mat-form-field>


            </div>

            <div class="form-column">
                <h3 class="form-subheader">Resulting Evaluations</h3>

                <ng-container *ngIf="!isMultiCrop; else resultsByCrop">
                    <table class="pv-table">
                        <thead>
                            <tr>
                                <th>Label</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Size</th>
                                <th>Storage</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let evalu of dedupedResultingEvaluations">
                                <td>{{ evalu.label }}</td>
                                <td>{{ evalu.evalStartDate | amDateFormat: 'll' }}</td>
                                <td>{{ evalu.evalEndDate | amDateFormat: 'll' }}</td>
                                <td>{{ evalu.size }}</td>
                                <td>
                                    <span *ngFor="let storage of evalu.storageRegime">
                                        {{ storage.duration }}&nbsp;@&nbsp;{{ storage.temp }}&#8451;,
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </ng-container>

                <ng-template #resultsByCrop>
                    <div *ngFor="let cropGroup of resultingEvaluationsByCrop" style="margin-bottom: 16px;">
                        <p style="font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.02em; margin: 0 0 4px; color: rgba(0, 0, 0, 0.6);">{{ cropGroup.cropLabel }}</p>
                        <table class="pv-table">
                            <thead>
                                <tr>
                                    <th>Label</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Size</th>
                                    <th>Storage</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let evalu of cropGroup.evaluations">
                                    <td>{{ evalu.label }}</td>
                                    <td>{{ evalu.evalStartDate | amDateFormat: 'll' }}</td>
                                    <td>{{ evalu.evalEndDate | amDateFormat: 'll' }}</td>
                                    <td>{{ evalu.size }}</td>
                                    <td>
                                        <span *ngFor="let storage of evalu.storageRegime">
                                            {{ storage.duration }}&nbsp;@&nbsp;{{ storage.temp }}&#8451;,
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p class="form-caption" *ngIf="cropGroup.schedule as schedule" style="margin-top: 4px;">
                            ** {{ schedule.label }} description: {{ schedule.description }}
                        </p>
                    </div>
                </ng-template>

                <p class="form-caption" *ngIf="!isMultiCrop && resultingSchedule as schedule">
                    ** {{ schedule.label }} description: {{ schedule.description }}
                </p>

                <p class="form-caption" *ngIf="resultingProtocol as protocol">
                    *** {{ protocol.title }} applied at all stages: {{ protocol.description }}
                </p>


            </div>

        </form>

    </div>

    <pv-form-actions mat-dialog-actions [form]="form" [status]="state.status" (cancel)="cancel()">

    </pv-form-actions>

</ng-container>
